import type { DragEndEvent, DragMoveEvent } from '@dnd-kit/react';
import type { DropCell } from './board-types';

export interface DropGeometry {
  cellSize: number;
  gap: number;
  padding: number;
  /** Board's own `size` (grid is `size × size`) — matches Board's default of 11. */
  size?: number;
}

/**
 * The board cell under a tile dropped from outside the board — e.g. out of a `TileRack` — via a
 * `DragEndEvent` whose target is Board's droppable (`Board`'s `dropId`, `'board'` by default).
 *
 * Anchors on the centre of the dragged tile's first character, so the tile's first cell — not whichever
 * cell the pointer happened to be over — lands at the drop point. The centre rather than the corner
 * because the tile in the air is sized by the rack, not the board: a corner drifts off the tile's
 * middle whenever the two differ, and with it the point where the drop tips from one cell to the next.
 * One character's worth is measured from the tile's own height, so a multi-character tile anchors on
 * its first cell rather than its middle. Unlike Board's own internal drag (see `board.tsx`), this does not
 * track which cell of a multi-character tile was grabbed; a tile dragged in from outside always
 * anchors by its first cell.
 *
 * The tile is read as it was picked up — its first character in the top-left corner of its box — moved
 * by as far as the pointer has travelled since. Not from the box the overlay is drawn in now: a tile
 * turned in the air is drawn a different shape, which would otherwise move the cell it is read as
 * over, and with it whatever turned it. Where the pickup shape or pointer is missing, the drawn box is
 * read instead.
 *
 * Mid-drag, pass the `DragMoveEvent`: its `to` is where the pointer is going, one move ahead of the
 * operation's own position.
 *
 * Also reports the `offset` the tile landed at — how far past the cell's own corner its corner sat,
 * in cells, each component in `[-0.5, 0.5]`. Rounding to a cell throws that away, and it is what says
 * which way a tile leans when it comes down on a cell another tile already holds: see
 * `resolveLanding`, which slides it out that way.
 *
 * Returns `null` when the event carries no board or tile shape (the drop wasn't over the board, or the
 * tile's shape hasn't been measured yet) — including for a drop that registers as "over" the board's
 * droppable but, on the actual measured geometry, does not land on any real cell. That gap between
 * "counts as a collision" and "is actually over a cell" is real: dnd-kit's default collision detection
 * can register the board as the target from a partial-pixel overlap at its very edge, or from a drag
 * too brief for the drag overlay's own shape measurement — which is what the operation's shape tracks,
 * in preference to the origin node's — to have caught up with the pointer. Every caller needs the
 * bounds check for correctness, not just convenience, so it lives here rather than in each caller.
 */
export function cellFromDragEvent(
  event: DragEndEvent | DragMoveEvent,
  { cellSize, gap, padding, size = 11 }: DropGeometry,
): DropCell | null {
  const { operation } = event;
  const boardRect = operation.target?.shape?.boundingRectangle;
  const tileRect = pickedUpRect(event) ?? operation.shape?.current.boundingRectangle;
  if (!boardRect || !tileRect) return null;

  // Where the first character's centre sits, expressed as the top-left of a board-sized cell centred on it.
  const half = (tileRect.height - cellSize) / 2;
  const x = (tileRect.left + half - boardRect.left - padding) / (cellSize + gap);
  const y = (tileRect.top + half - boardRect.top - padding) / (cellSize + gap);
  const col = Math.round(x);
  const row = Math.round(y);
  if (row < 0 || row >= size || col < 0 || col >= size) return null;

  return { row, col, offset: { row: y - row, col: x - col } };
}

type Rect = { left: number; top: number; height: number };

/** The tile's box at pickup, carried along by the pointer's travel since. */
function pickedUpRect({ operation, ...event }: DragEndEvent | DragMoveEvent): Rect | null {
  const initial = operation.shape?.initial?.boundingRectangle;
  const start = operation.position?.initial;
  const pointer = ('to' in event ? event.to : undefined) ?? operation.position?.current;
  if (!initial || !start || !pointer) return null;
  return {
    left: initial.left + pointer.x - start.x,
    top: initial.top + pointer.y - start.y,
    height: initial.height,
  };
}
